import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import loadable from '@loadable/component';
import { CssBaseline } from '@mui/material';
import { BrowserRouter as Router } from 'react-router-dom';
import getClient from 'apolloClient';
import { getModuleState } from 'services/auth';
import { ApolloProvider, useLazyQuery } from '@apollo/client';
import { selectors, actions as userActions } from '../services/user';
import { GET_PROFILE_INFO } from 'pages/ProfileSettingsPage/gqlSchemes/getProfileInfo';
import useGetAuth from 'useGetAuth';
import useGetAlertsPage from 'pages/AlertsPage/_service/hooks/useGetAlertsPage';
import LoadingPage from 'components/LoadingPage';
import { useSentryUser } from 'lib/hooks/useSentryUser';
import InactivityMonitor from 'components/InactivityMonitor';

const TeamMemberRouting = loadable(() => import('./TeamMemberRouting'), {
  fallback: <LoadingPage />
});
const AdminRouting = loadable(() => import('./AdminRouting'), {
  fallback: <LoadingPage />
});
const LoginRouting = loadable(() => import('./LoginRouting'), {
  fallback: <LoadingPage />
});

export const ConfirmDeleteDialogContext = React.createContext({});

const RouterComponent = ({ handleAuth }) => {
  const { i18n } = useTranslation();

  const authState = useSelector(getModuleState);
  const user = useSelector(selectors.getUser);
  const [fetchProfile, { data, loading, error }] = useLazyQuery(
    GET_PROFILE_INFO
  );
  const dispatch = useDispatch();

  const { getLazyQueryAlerts } = useGetAlertsPage(0, 1, {}, true);
  useSentryUser();

  useEffect(() => {
    const currentMe = { ...data?.user?.dashboard?.me };
    if (currentMe && currentMe?.newRoles?.length) {
      dispatch(userActions.setUserInfo({ user: currentMe }));
    }
    if (currentMe && currentMe?.selectedLanguage) {
      i18n.changeLanguage(
        currentMe?.selectedLanguage?.includes('-')
          ? currentMe?.selectedLanguage?.split('-')[0] +
              '-' +
              currentMe?.selectedLanguage?.split('-')[1].toUpperCase()
          : currentMe?.selectedLanguage
      );
    }
    getLazyQueryAlerts({
      variables: {
        page: 0,
        perPage: 1,
        filter: {
          assignedToMe: true,
          title: '',
          status: 'triggered',
        },
      },
      onCompleted: (res) => {
        dispatch(
          userActions.setTriggeredAlerts(
            res.alert.alertsPage.pageInfo.totalItems
          )
        );
      },
    });
  }, [data]);

  const Routing = useMemo(() => {
    const role = data?.user?.dashboard?.me?.newRoles.length
      ? data?.user?.dashboard?.me?.newRoles[0].type
      : 'unknown';

    if (!authState?.isAuthenticated) {
      if (user) {
        i18n.changeLanguage(
          user?.selectedLanguage?.includes('-')
            ? user?.selectedLanguage?.split('-')[0] +
                '-' +
                user?.selectedLanguage?.split('-')[1].toUpperCase()
            : user?.selectedLanguage
        );
      }
      return <LoginRouting handleAuth={handleAuth} />;
    } else {
      if (!loading && !error) {
        fetchProfile();
      }

      if (!user.id || loading) {
        return <LoadingPage />;
      } else {
        switch (role) {
          case 'unknown':
            return <LoginRouting handleAuth={handleAuth} />;
          case 'MEDICAL':
          case 'EXTERNAL':
            return <TeamMemberRouting />;
          case 'ADMINISTRATIVE':
            return <AdminRouting />;
          default:
            return <LoginRouting handleAuth={handleAuth} />;
        }
      }
    }
  }, [data, loading, user, authState, error]);

  return (
    <>
      {Routing}
      {authState?.isAuthenticated && <InactivityMonitor />}
    </>
  );
};

export default function Routing() {
  const authData = useSelector((state) => state.auth);

  const { handleAuth, withAccessToken, errorLink } = useGetAuth(authData);

  // Create the Apollo Client exactly once.
  //
  // Previously, getClient() was called on every render, which created a brand-new
  // Apollo Client every time the auth state changed (e.g. on token refresh).
  // That caused useLazyQuery hooks in child components to re-execute GET_ONE,
  // which returned pre-save backend data and overwrote the widget Redux state,
  // making newly-added widgets disappear from the Builder after a save that
  // required a token refresh.
  //
  // withAccessToken() and errorLink are now stable references (created once via
  // useCallback/useMemo inside useGetAuth with [] deps). They read the current
  // token from a ref internally, so new requests always use the latest token
  // without requiring the client to be recreated.
  const clientRef = useRef(null);
  if (!clientRef.current) {
    clientRef.current = getClient(withAccessToken(), errorLink);
  }

  return (
    <ApolloProvider client={clientRef.current}>
      <Router>
        <RouterComponent handleAuth={handleAuth} getClient={getClient} />
        <CssBaseline />
      </Router>
    </ApolloProvider>
  );
}
